// src/components/admin/InventoryDialog.jsx
import React, { useState } from 'react';
import { X, Plus, Minus } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { translations } from '../../utils/translations';
import { inventoryApi } from '../../api';

const InventoryDialog = ({ product, onClose, onSuccess }) => {
  const { language } = useApp();
  const t = translations[language];
  
  const [type, setType] = useState('in'); // 'in' 入库, 'out' 出库
  const [quantity, setQuantity] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!quantity || parseInt(quantity) <= 0) {
      alert(language === 'en' ? 'Please enter a valid quantity' :
            language === 'km' ? 'សូមបញ្ចូលចំនួនត្រឹមត្រូវ' :
            '请输入有效的数量');
      return;
    }

    setLoading(true);

    try {
      // change_type 必须是: 'restock'(入库), 'adjust'(调整), 'return'(退货)
      const adjustmentData = {
        product_id: product.id,
        quantity: type === 'in' ? parseInt(quantity) : -parseInt(quantity),
        change_type: type === 'in' ? 'restock' : 'adjust',
        notes: reason || (type === 'in' ? '入库' : '出库')
      };

      console.log('📦 提交库存调整:', adjustmentData);

      const res = await inventoryApi.adjust(adjustmentData);

      if (res.success) {
        console.log('✅ 库存调整成功');
        alert(language === 'en' ? 'Inventory updated successfully!' :
              language === 'km' ? 'បានធ្វើបច្ចុប្បន្នភាពស្តុកដោយជោគជ័យ!' :
              '库存调整成功！');
        onSuccess();
        onClose();
      } else {
        alert(res.message || t.operationFailed || '操作失败');
      }
    } catch (err) {
      console.error('❌ 库存调整失败:', err);
      alert(err.message || t.operationFailed || '操作失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 获取商品显示名称
  const getProductName = () => {
    switch (language) {
      case 'en':
        return product.name_en || product.name;
      case 'km':
        return product.name_km || product.name;
      default:
        return product.name;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-md">
        <div className="border-b p-6 flex justify-between items-center">
          <h3 className="text-xl font-bold">
            {language === 'en' ? 'Inventory Adjustment' :
             language === 'km' ? 'កែតម្រូវស្តុក' :
             '库存调整'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* 商品信息 */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-medium text-gray-800">{getProductName()}</p>
                <p className="text-sm text-gray-500">{t.barcode}: {product.barcode}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">
                  {language === 'en' ? 'Current Stock' :
                   language === 'km' ? 'ស្តុកបច្ចុប្បន្ន' :
                   '当前库存'}
                </p>
                <p className="text-2xl font-bold text-blue-600">{product.stock}</p>
              </div>
            </div>
          </div>

          {/* 操作类型 */}
          <div>
            <label className="block text-sm font-medium mb-2">
              {language === 'en' ? 'Operation Type' :
               language === 'km' ? 'ប្រភេទប្រតិបត្តិការ' :
               '操作类型'}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setType('in')}
                className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                  type === 'in'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Plus className="w-5 h-5 mr-2" />
                {language === 'en' ? 'Stock In' :
                 language === 'km' ? 'ចូលស្តុក' :
                 '入库'}
              </button>
              
              <button
                type="button"
                onClick={() => setType('out')}
                className={`flex items-center justify-center px-4 py-3 rounded-lg border-2 transition-colors ${
                  type === 'out'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Minus className="w-5 h-5 mr-2" />
                {language === 'en' ? 'Stock Out' :
                 language === 'km' ? 'ចេញស្តុក' :
                 '出库'}
              </button>
            </div>
          </div>

          {/* 数量 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {language === 'en' ? 'Quantity' :
               language === 'km' ? 'ចំនួន' :
               '数量'}
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="10"
              min="1"
              required
            />
            <p className="mt-1 text-sm text-gray-500">
              {language === 'en' ? 'New Stock' :
               language === 'km' ? 'ស្តុកថ្មី' :
               '调整后库存'}: {
                quantity 
                  ? (type === 'in' ? product.stock + parseInt(quantity) : product.stock - parseInt(quantity))
                  : product.stock
              }
            </p>
          </div>

          {/* 备注 */}
          <div>
            <label className="block text-sm font-medium mb-1">
              {language === 'en' ? 'Reason (Optional)' :
               language === 'km' ? 'មូលហេតុ (ស្រេចចិត្ត)' :
               '备注 (可选)'}
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              rows="3"
              placeholder={language === 'en' ? 'Enter reason for adjustment...' :
                          language === 'km' ? 'បញ្ចូលមូលហេតុនៃការកែតម្រូវ...' :
                          '输入调整原因...'}
            />
          </div>

          {/* 警告提示 */}
          {type === 'out' && quantity && parseInt(quantity) > product.stock && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-700">
                ⚠️ {language === 'en' ? 'Warning: Insufficient stock!' :
                      language === 'km' ? 'ព្រមាន: ស្តុកមិនគ្រប់គ្រាន់!' :
                      '警告: 库存不足！'}
              </p>
            </div>
          )}

          {/* 按钮 */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              disabled={loading || (type === 'out' && quantity && parseInt(quantity) > product.stock)}
              className={`px-4 py-2 rounded-lg transition-colors ${
                type === 'in'
                  ? 'bg-green-600 hover:bg-green-700 text-white'
                  : 'bg-red-600 hover:bg-red-700 text-white'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading 
                ? (language === 'en' ? 'Processing...' : language === 'km' ? 'កំពុងដំណើរការ...' : '处理中...')
                : t.confirm}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryDialog;